/* eslint-disable */
import React, {
  useState,
  type FC,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  type ChangeEvent,
} from 'react';
import { IChatProps, type IComplaintsProps } from 'types/pages';
import { TextField, Box, useTheme } from '@mui/material';
import { debounce } from 'lodash';
import Grid from '@mui/material/Grid';
import Buttons from 'components/elements/Buttons';
import { strings } from 'i18n/Strings';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSnackBarFailed,
  setSnackBarInfo,
  setSnackBarSuccess,
} from 'store/snackBar';
import { type RootState } from 'store/rootReducer';
import { createChatConversation, uploadChatImage } from 'service/ChatService';
import Autocomplete from '@mui/material/Autocomplete';
import { getAllCustomerEmails } from 'service/CustomerService';
import { colors } from '@mui/material';
import { colorTheme } from 'theme';
import { commonRegex, customerChatsConstants } from 'constants/constantText';
import { CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';

interface IChatsError {
  firstName: string;
  lastName: string;
  email: string;
  description: string;
  selectedValue: string;
}

interface IAddChatProps {
  onClose: () => void;
  getConversationDetails: () => void;
  onExistingConversation?: (userId: number) => void;
}

interface Customer {
  email: string;
  id: number;
}

const AddChat: FC<IAddChatProps> = ({
  onClose,
  getConversationDetails,
  onExistingConversation,
}): JSX.Element => {
  const theme = useTheme();
  const { id } = useParams();
  const colors = colorTheme(theme.palette.mode);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [customerEmail, setCustomerEmail] = useState<Customer[]>([]);
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState('');
  const [isLoader, setIsLoader] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [formError, setFormError] = useState<IChatProps>({
    firstName: '',
    lastName: '',
    email: '',
    description: '',
  });
  const dispatch = useDispatch();
  const { empId } = useSelector((state: RootState) => state.onboarding);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current != null) {
      inputRef.current.focus();
    }
    handlegetAllEmails();
  }, []);

  const handlegetAllEmails = async (): Promise<void> => {
    try {
      const emails = await getAllCustomerEmails();
      const res: { basicInfo: Customer[] } = emails;

      if (res?.basicInfo) {
        setCustomerEmail(res?.basicInfo);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleInputChange = (
    type: string,
    e: { target: { value: string } },
  ): undefined | string => {
    switch (type) {
      case 'firstName': {
        setFirstName(e.target.value);
        return;
      }
      case 'lastName': {
        setLastName(e.target.value);
        return;
      }
      case 'email': {
        setEmail(e.target.value);
        return;
      }
      case 'description': {
        setDescription(e.target.value);
        return;
      }

      default:
        return '';
    }
  };

  const handleValidation = (
    type: string,
    e: { target: { value: string } },
  ): void => {
    const err: IChatProps = {};
    switch (type) {
      case 'firstName': {
        if (e.target.value === '') {
          err.firstName = strings.displayText.firstNameRequiredText;
        }
        setFormError({ ...formError, firstName: err.firstName });
        break;
      }
      case 'lastName': {
        if (e.target.value === '') {
          err.lastName = strings.displayText.lastNameRequiredText;
        }
        setFormError({ ...formError, lastName: err.lastName });
        break;
      }
      case 'email': {
        const emailRegex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (e.target.value === '') {
          err.email = strings.displayText.emailEmptyText;
        }
        if (!emailRegex.test(e.target.value)) {
          err.email = strings.displayText.invalidEmailText;
        }
        setFormError({ ...formError, email: err.email });
        break;
      }

      case 'description': {
        if (e.target.value === '') {
          err.description = strings.displayText.desccriptionRequiredText;
        }
        setFormError({ ...formError, description: err.description });
        break;
      }
      default:
        break;
    }
  };

  const debounceHandler = useCallback(
    debounce((type: string, e: { target: { value: string } }) => {
      handleValidation(type, e);
    }, 600),
    [],
  );

  const validateForm = (): boolean => {
    const err: IChatsError = {
      firstName: '',
      lastName: '',
      email: '',
      description: '',
      selectedValue: '',
    };

    if (firstName.trim() === '') {
      err.firstName = 'First name is required';
    }

    if (selectedValue === null) {
      err.email = strings.displayText.emailIsRequired;
    }

    if (lastName.trim() === '') {
      err.lastName = 'Last name is required';
    }

    if (description.trim() === '') {
      err.description = 'Description is required';
    }

    setFormError({ ...err });
    return Object.keys(err).every((key) => err[key].length === 0);
  };

  const handleFileInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const reader = new FileReader();

      reader.onload = (e) => {
        // const imageSrc = e.target?.result ;
        void uploadChatImage(file, Number(id)).then((res) => {
          setImageFile(res?.chatImage);
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAutocompleteChange = (event, newValue) => {
    setFormError((prevErrors) => ({
      ...prevErrors,
      email: '',
    }));

    if (newValue) {
      const isValidEmail = commonRegex.emailPattern.test(newValue?.email);
      if (!isValidEmail) {
        setFormError((prevErrors) => ({
          ...prevErrors,
          email: strings.displayText.invalidEmailFormat,
        }));
      }
    }

    setSelectedValue(newValue);
  };

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const formFirstName = data.get('firstName')?.toString() ?? '';
    const formLastName = data.get('lastName')?.toString() ?? '';
    const formDescription = data.get('description')?.toString() ?? '';

    const value = {
      email: selectedValue?.email,
      userId: selectedValue?.id,
      mmEmployeeId: empId,
      userName: `${formFirstName} ${formLastName}`,
      messages: formDescription,
      createdBy: empId,
      createdByType: customerChatsConstants.employee,
      senderType: customerChatsConstants.employee,
      imageUri: imageFile,
    };
    const isValid = validateForm();

    if (isValid) {
      setIsLoader(true);
      void (async () => {
        // create chat Conversation service here
        createChatConversation(value)
          .then((res) => {
            dispatch(
              setSnackBarSuccess({
                snackBarMessage: strings.displayText.complaintCreatedText,
              }),
            );

            getConversationDetails();
            setIsLoader(false);
            onClose();
          })
          .catch((err) => {
            setIsLoader(false);

            // Check if conversation already exists
            const errorMessage = err?.response?.data?.msg || '';
            const conversationExists = err?.response?.data?.conversationExists;

            if (conversationExists && onExistingConversation) {
              // Conversation already exists
              dispatch(
                setSnackBarInfo({
                  snackBarMessage: 'Conversation already exists.',
                }),
              );
              onExistingConversation(selectedValue?.id);
              onClose();
            } else {
              dispatch(setSnackBarFailed({ snackBarMessage: errorMessage }));
              console.error(`Something went wrong: ${String(err)}`);
            }
          });
      })();
    }
  };

  const customStyles = {
    errorTextStyle: {
      '& p': {
        color: colors.primaryRed[100],
      },
      mb: 2,
    },
    mainBox: {
      maxWidth: '50rem',
    },
    submitBtn: { py: '0.8rem', mt: '1rem' },
  };

  return (
    <Fragment>
      <Box sx={customStyles.mainBox}>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                inputRef={inputRef}
                margin="normal"
                required
                fullWidth
                label={strings.displayText.firstName}
                name="firstName"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleInputChange('firstName', e);
                  debounceHandler('firstName', e);
                }}
                sx={{
                  '& p': {
                    color: 'red',
                  },
                  mb: 2,
                }}
                helperText={formError?.firstName}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                // inputRef={inputRef}
                margin="normal"
                required
                fullWidth
                label={strings.displayText.lastName}
                name="lastName"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleInputChange('lastName', e);
                  debounceHandler('lastName', e);
                }}
                sx={{
                  '& p': {
                    color: 'red',
                  },
                  mb: 2,
                }}
                helperText={formError?.lastName}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                options={customerEmail}
                getOptionLabel={(option) => option.email}
                value={selectedValue}
                fullWidth
                onChange={handleAutocompleteChange}
                sx={{ mt: 2 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={strings.displayText.emailAddress}
                    required
                    sx={customStyles.errorTextStyle}
                    helperText={formError?.email}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Description"
                name="description"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleInputChange('description', e);
                  debounceHandler('description', e);
                }}
                sx={{
                  '& p': {
                    color: 'red',
                  },
                  mb: 2,
                }}
                helperText={formError?.description}
              />
            </Grid>

            <Grid item xs={6} my={2}>
              <input
                type="file"
                onChange={handleFileInputChange}
                accept="image/*,application/pdf"
              />
            </Grid>
          </Grid>
          <Buttons
            disabled={
              isLoader ||
              firstName.trim() === '' ||
              lastName.trim() === '' ||
              selectedValue === null ||
              description.trim() === ''
            }
            style={customStyles.submitBtn}
            fullWidth={true}
            type="submit"
            endIcon={isLoader ? <CircularProgress size={15} /> : null}
          >
            {strings.displayText.saveText}
          </Buttons>
        </Box>
      </Box>
    </Fragment>
  );
};

export default AddChat;
